'use client'

import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

type ToastType = 'success' | 'error'

interface ToastMessage {
  text: string
  type: ToastType
}

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  UNABLE_TO_VERIFY_EMAIL:
    'A megerősítő link lejárt vagy érvénytelen. Kérjük, kérj új megerősítő e-mailt.',
  EXPIRED_TOKEN: 'A megerősítő link lejárt. Kérjük, kérj új megerősítő e-mailt.',
  INVALID_TOKEN: 'A megerősítő link érvénytelen. Kérjük, kérj új megerősítő e-mailt.',
  EXPIRED_TOKEN_ERROR: 'A megerősítő link lejárt. Kérjük, kérj új megerősítő e-mailt.',
}

function getToastMessage(searchParams: URLSearchParams): ToastMessage | null {
  // Errors take priority over success params
  const error = searchParams.get('error')
  if (error) {
    return {
      text: AUTH_ERROR_MESSAGES[error] || 'Hiba történt. Kérjük, próbáld újra.',
      type: 'error',
    }
  }

  if (searchParams.get('verified') === 'true') {
    return { text: 'E-mail címed sikeresen megerősítve!', type: 'success' }
  }

  if (searchParams.get('registered') === 'true') {
    return { text: 'Sikeres regisztráció! Már nézheted a CreatorTV műsorait.', type: 'success' }
  }

  return null
}

export default function VerificationToast() {
  const searchParams = useSearchParams()
  const [toast, setToast] = useState<ToastMessage | null>(() => getToastMessage(searchParams))

  useEffect(() => {
    if (!toast) return

    // Clean up URL params without triggering a Next.js navigation/remount
    const url = new URL(window.location.href)
    url.searchParams.delete('verified')
    url.searchParams.delete('registered')
    url.searchParams.delete('error')
    window.history.replaceState(null, '', url.pathname + url.search)

    const timer = setTimeout(() => setToast(null), toast.type === 'error' ? 8000 : 4000)
    return () => clearTimeout(timer)
  }, [toast])

  if (!toast) return null

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-6 right-0 left-0 z-50 mx-auto flex w-fit max-w-[90vw] items-center gap-3 rounded-lg px-5 py-3 text-sm font-medium text-white shadow-lg ${
        toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
      }`}
      style={{ animation: 'toast-in 0.3s ease-out' }}
    >
      {toast.text}
      <button
        onClick={() => setToast(null)}
        aria-label="Bezárás"
        className="ml-1 shrink-0 cursor-pointer opacity-70 transition-opacity hover:opacity-100"
      >
        &#x2715;
      </button>
    </div>
  )
}
